import { Delta } from 'quill/core';

import { BlueSageCardOnFileFragment } from 'src/generated/graphql.generated';

export type CreateTransactionStep =
    | 'holds'
    | 'targets'
    | 'events'
    | 'amount'
    | 'method'
    | 'details'
    | 'complete';

export const CREATE_TRANSACTION_STEPS: CreateTransactionStep[] = [
    'targets', 'amount', 'method', 'details',
];

export type CreateTransactionMethod =
    | 'card'
    | 'terminal'
    | 'preauth'
    | 'payLink'
    | 'cash'
    | 'check'
    | 'recordCard'
    | 'creditCard'
    | 'masterCard'
    | 'visa'
    | 'amex'
    | 'debit'
    | 'other'
    | 'squareCheckout';

// Requirement: sale and authorization are two labelled buttons, never a mode
// toggle — a wrong silent default captures money early or leaves a hold to
// expire unclaimed. Carried by the press, never stored.
export type BlueSageTerminalIntent = 'sale' | 'preauth';

// Requirement: a transaction is a deposit or a payment for ONE invoice, never
// both and never several. An invoice is raised once the service is rendered, so
// money against one is by definition not a deposit; `transactions.create` takes
// a single invoiceId, and a second link double-counts the job's paid total.
export type CreateTransactionTarget =
    | { kind: 'deposit' }
    | { kind: 'invoice'; invoiceId: string }
    | { kind: 'newInvoice' };

export type SelectedCard =
    | { kind: 'new' }
    | { kind: 'onFile'; token: string };

// Requirement: the unusable types are carried too. "This zone turned Check off"
// and "this zone has no Check" are different sentences to show an operator, and
// a list filtered to the usable rows cannot tell them apart.
export interface PaymentTypeConfig {
    id: string;
    // The stable identity across zones. Match on this, never on `name`, which is
    // the zone's own display label.
    key: string;
    name: string;
    enabled: boolean;
    // Any truthy value, not just a known processor: `transactions.create` rejects
    // a provider-backed type recorded as anything but `pending`, so a type
    // carrying one cannot be recorded against here.
    provider: string | null;
}

export type PaymentTypeUnavailable = 'missing' | 'disabled';

export const createTransactionDialogInitialState = {
    open: false as boolean,
    step: 'targets' as CreateTransactionStep,

    // Requirement: a transaction created for one opening of the dialog must
    // never be handed to the next. A create can still be in flight when the
    // dialog is reopened on another job, and `open` reads true for both.
    session: 0 as number,

    jobId: undefined as string | undefined,
    customerId: undefined as string | undefined,
    currency: 'USD' as string,

    target: undefined as CreateTransactionTarget | undefined,

    newInvoiceEventIds: [] as string[],
    askedForInvoiceEvents: false as boolean,
    raisedInvoiceId: undefined as string | undefined,

    amountMinor: 0 as number,

    method: undefined as CreateTransactionMethod | undefined,

    paymentTypeId: undefined as string | undefined,

    // Requirement: the rate is for quoting only — the CRM records a differential
    // nowhere, and money always comes from the event's own amounts.
    differentialPct: null as number | null,
    differentialPctKnown: false as boolean,

    // Requirement: a hold is offered only where an admin switched holds on for
    // the zone (`bluesage.holdsEnabled`): BlueSage refuses every hold until it
    // has enabled them, and a choice that always fails is not one to offer.
    holdsEnabled: false as boolean,

    // Requirement: a SALE is the default, every time. Holding instead of
    // charging is a deliberate choice made on the button itself, and it is
    // forgotten when the dialog closes or the payment method changes.
    holdInsteadOfCharge: false as boolean,

    paymentTypes: [] as PaymentTypeConfig[],
    selectedPaymentTypeId: undefined as string | undefined,

    // Requirement: an empty list must not be reported to the operator as a
    // zone misconfiguration before the read has come back, or at all when it
    // failed. A network error is not a statement about the zone's settings.
    paymentTypesLoaded: false as boolean,
    paymentTypesFailed: false as boolean,

    // Requirement: `Transaction.remarks` is rendered back through a read-only
    // Quill editor and the backend appends to it, so the shape is not ours to
    // choose — a plain string is the wrong one for the components that read it.
    remarks: new Delta(),

    // Requirement: the customer gets a receipt unless the operator says
    // otherwise, and none at all where the record names no way to reach them.
    sendReceipt: true as boolean,

    payUrl: undefined as string | undefined,

    // Requirement: a delivered pay link is withdrawn through BlueSage — unlike
    // `createdTransactionId`, which is an unproven row closed on failure.
    payLinkTransactionId: undefined as string | undefined,

    // Requirement: paying is not consent to store a card. Vaulting is an
    // explicit opt-in and starts unticked for every dialog session.
    vaultOnPayLink: false as boolean,

    // A terminal push creates the row and then pushes against it, and the push
    // can be refused outright by a busy or offline device — nothing charged, but
    // the row already on the job. Holding the id is what closes it.
    createdTransactionId: undefined as string | undefined,

    // The BlueSage-served card form. Its token is single-use and expires in
    // fifteen minutes; no PAN reaches this app.
    embedUrl: undefined as string | undefined,
    cardToken: undefined as string | undefined,
    cardLast4: undefined as string | undefined,

    openingCardForm: false as boolean,

    // Requirement: charging is not consent to store a card. Saving is an
    // explicit opt-in and resets to unticked whenever the card it applied to
    // goes away — a box ticked for one card is not an answer about the next.
    saveCard: false as boolean,

    cardsOnFile: [] as BlueSageCardOnFileFragment[],

    selectedCard: undefined as SelectedCard | undefined,

    // Resolved against the job's live holds before anything is sent, never
    // treated as a standing snapshot of the job's open hold.
    openPreauthTransactionId: undefined as string | undefined,

    creating: false as boolean,
    error: undefined as string | undefined,

    jobLoadFailed: false as boolean,
};

export type CreateTransactionDialogState =
    typeof createTransactionDialogInitialState;
